// src/features/streams/subaccounts/SubAccountStreamUtils.ts

import { bcInt } from '@lib2/model/util/string/BaseConverter';
import { StreamUtils } from '../StreamUtils';
import type { IBackendLiveData, CustomerAllData } from '@/lib/types/Customer';
import type {
  StreamViewData,
  StreamLiveStatus,
  RealtimeStreamData,
  SubAccountStreamRow,
  SubAccountStreamSortKey,
} from '@/lib/types/Stream';
import type { SortDirection } from '@/lib/types/common';

/** Sort rank for live status: on-air rows first when sorting descending. */
const LIVE_STATUS_RANK: Record<StreamLiveStatus, number> = {
  recording: 2,
  live: 1,
  offline: 0,
};

export interface SubAccountStreamSummary {
  streamCount: number;
  liveCount: number;
  recordingCount: number;
  accountCount: number;
}

export class SubAccountStreamUtils {
  /** Map sub-account cId → label for the Account column. */
  static buildAccountLabelMap(subcustomers: CustomerAllData[]): Map<number, string> {
    return new Map(subcustomers.map((s) => [s.cfg.cId, s.cfg.label]));
  }

  /**
   * The consolidated endpoint also returns the master account's own streams
   * (they used to render as "Name Not Available"). This view monitors
   * sub-accounts only — the master's streams live in "This Account".
   */
  static excludeMasterStreams(
    streams: StreamViewData[],
    masterCId: number | null,
  ): StreamViewData[] {
    if (masterCId === null) return streams;
    return streams.filter((s) => s.media.cId !== masterCId);
  }

  /** Build grid rows: the standard stream row plus owning-account identity. */
  static buildRows(
    streams: StreamViewData[],
    liveStatusMap: Map<number, RealtimeStreamData>,
    backendMap: Map<number, IBackendLiveData>,
    accountLabelMap: Map<number, string>,
  ): SubAccountStreamRow[] {
    return streams.map((stream) => {
      const cId = stream.media.cId;
      return {
        // No link config: the sub-account grid has no thumbnail column
        ...StreamUtils.buildRow(stream, liveStatusMap, backendMap, null),
        cId,
        accountLabel: accountLabelMap.get(cId) ?? 'Name Not Available',
        accountDisplayId: bcInt.getStr(cId),
      };
    });
  }

  /** Apply the live-only toggle and text search (title, IDs, account label). */
  static filterRows(
    rows: SubAccountStreamRow[],
    options: { liveOnly: boolean; searchText: string },
  ): SubAccountStreamRow[] {
    const needle = options.searchText.trim().toLowerCase();
    if (!options.liveOnly && needle === '') return rows;
    return rows.filter((row) => {
      if (options.liveOnly && row.liveStatus === 'offline') return false;
      if (needle === '') return true;
      return (
        row.title.toLowerCase().includes(needle) ||
        row.displayId.toLowerCase().includes(needle) ||
        row.accountLabel.toLowerCase().includes(needle) ||
        row.accountDisplayId.toLowerCase().includes(needle)
      );
    });
  }

  /** Sort rows by key/direction. Ties resolve account → title so output is stable. */
  static sortRows(
    rows: SubAccountStreamRow[],
    key: SubAccountStreamSortKey,
    direction: SortDirection,
  ): SubAccountStreamRow[] {
    const dir = direction === 'asc' ? 1 : -1;
    const byAccountThenTitle = (a: SubAccountStreamRow, b: SubAccountStreamRow) =>
      a.accountLabel.localeCompare(b.accountLabel) || a.title.localeCompare(b.title);

    const primary = (a: SubAccountStreamRow, b: SubAccountStreamRow): number => {
      switch (key) {
        case 'account':
          return a.accountLabel.localeCompare(b.accountLabel);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'liveStatus':
          return LIVE_STATUS_RANK[a.liveStatus] - LIVE_STATUS_RANK[b.liveStatus];
        case 'location':
          return a.locationLabel.localeCompare(b.locationLabel);
        case 'status':
          return a.statusLabel.localeCompare(b.statusLabel);
        case 'published':
          return (a.published ?? 0) - (b.published ?? 0);
        case 'updated':
          return a.updated - b.updated;
      }
    };

    return [...rows].sort((a, b) => dir * primary(a, b) || byAccountThenTitle(a, b));
  }

  /** Header counts. accountCount = distinct sub-accounts that own at least one stream. */
  static summarize(rows: SubAccountStreamRow[]): SubAccountStreamSummary {
    const accounts = new Set<number>();
    let liveCount = 0;
    let recordingCount = 0;
    for (const row of rows) {
      accounts.add(row.cId);
      if (row.liveStatus !== 'offline') liveCount++;
      if (row.liveStatus === 'recording') recordingCount++;
    }
    return {
      streamCount: rows.length,
      liveCount,
      recordingCount,
      accountCount: accounts.size,
    };
  }
}
